<script setup>

import {onMounted, ref} from 'vue';
import fileService from '@/services/FileService';
import CustomDropzone from '@/components/forms/CustomDropzone.vue';
import {useI18n} from 'vue-i18n';
import {getApiBaseUrl} from '@/helper';

const {t} = useI18n()
const props = defineProps({
  fileable: String,
  fileableId: {
    type: Number,
    default: null,
  },
  dropzoneDisabled: Boolean,
  singleFile: {
    type: Boolean,
    default: false,
  },
  acceptedFiles: {
    type: String,
    default: '.jpg, .png, .pdf, .docx, .xlsx',
    // default: 'image/*',
  },
  showFilePreview: {
    type: Boolean,
    default: true,
  },
  // When replacing a PDF template, controls whether the existing annotation
  // layer (form fields, signatures) is preserved. Only relevant for PDF uploads.
  keepAnnotations: {
    type: Boolean,
    default: true,
  },
});

const files = ref([]);
const AITypes = ['App/Models/Project'];

function uploadMultipleSuccess(files) {
  if (props.showFilePreview) {
    getFiles();
  }
}

function onUploadMultipleError(error) {
  console.log(error);
}

function onUploadSuccess(file) {
  if (props.showFilePreview) {
    getFiles();
  }
}

function onUploadError(error) {
  console.log(error);
}

function onSend() {
  console.log('onSend called')
}

function getFiles() {
  if (props.showFilePreview) {
    fileService.getFilesByEntity(props.fileable, props.fileableId)
        .then((response) => {
          files.value = response;
        })
        .catch((error) => {
          console.log(error);
        });
  }
}

function handleDeleteFile(file) {
  fileService.deleteFile(file.id)
      .then(() => {
        getFiles();
      })
      .catch((error) => {
        console.log(error);
      });
}

async function downloadFile(file) {
  fileService.downloadFileByPlatform(file.base64, file.name, file.extension);
}

function generateFileSummary(file, fileableId) {
  emitter.emit('generateFileSummary', {file, fileableId})
}

function showFileSummary(file, fileableId) {
  emitter.emit('showFileSummary', {file, fileableId})
}

function generateFileTasks(file, fileableId) {
  emitter.emit('generateFileTasks', {file, fileableId})
}

onMounted(() => {
  getFiles();
});

</script>

<template>
  <div v-if="!dropzoneDisabled">
    <CustomDropzone :dropzone-options-input="{
        url: getApiBaseUrl() + '/api/files/upload',
        acceptedFiles: props.acceptedFiles.replaceAll(' ', ''),
        maxFiles: props.singleFile ? 1 : 20,
        uploadMultiple: !props.singleFile,
        paramName: props.singleFile ? 'file' : 'files',
        params: {
          'fileable': props.fileable,
          'fileable_id': props.fileableId
        },
        init() {
          this.on('sendingmultiple', function (file, xhr, formData) {
            formData.append('fileable_type', props.fileable);
            formData.append('fileable_id', props.fileableId);
            formData.append('keep_annotations', props.keepAnnotations ? '1' : '0');
          });
          this.on('sending', function (file, xhr, formData) {
            formData.append('fileable_type', props.fileable);
            formData.append('fileable_id', props.fileableId);
            // Pass annotation preference so backend knows whether to clear PDF annotations on replacement
            formData.append('keep_annotations', props.keepAnnotations ? '1' : '0');
          });
        }
      }" :previews-disabled="props.showFilePreview" :upload-multiple-success="uploadMultipleSuccess" :upload-multiple-error="onUploadMultipleError" :upload-success="onUploadSuccess" :upload-error="onUploadError" :on-send="onSend"/>
  </div>
  <div v-if="props.showFilePreview && files.length > 0" class="file-preview-container">
    <div class="file-preview" v-for="file in files" :key="file.id">
      <span class="file-icon" :class="'file-icon__' + file.extension"> {{ file.extension }} </span>
      <span class="overflow-hidden overflow-ellipsis whitespace-nowrap flex-1"> {{ file.name }} </span>
      <div class="dropdown dropdown-start">
        <div tabindex="0" role="button" class="ml-1"><span class="mdi mdi-chevron-down text-xl"></span></div>
        <ul tabindex="0" class="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 shadow">
          <li @click="downloadFile(file)" class="hover:bg-base-300 py-2 px-4 block whitespace-no-wrap cursor-pointer">
            {{ t('fileListComponentOld.download') }}
          </li>
          <li v-if="AITypes.includes(file.model_type.replaceAll('\\', '/')) && props.fileableId && !file.ai_summary" @click="generateFileSummary(file, props.fileableId)" class="hover:bg-base-300 py-2 px-4 block whitespace-no-wrap cursor-pointer">
            {{ t('fileListComponentOld.generate_report') }}
          </li>
          <li v-if="AITypes.includes(file.model_type.replaceAll('\\', '/')) && props.fileableId && file.ai_summary" @click="generateFileSummary(file, props.fileableId)" class="hover:bg-base-300 py-2 px-4 block whitespace-no-wrap cursor-pointer">
            {{ t('fileListComponentOld.view_report') }}
          </li>
          <li v-if="AITypes.includes(file.model_type.replaceAll('\\', '/')) && props.fileableId" @click="generateFileTasks(file, props.fileableId)" class="hover:bg-base-300 py-2 px-4 block whitespace-no-wrap cursor-pointer">
            {{ t('fileListComponentOld.generate_tasks') }}
          </li>
          <li @click="handleDeleteFile(file)" class="hover:bg-error py-2 px-4 block whitespace-no-wrap cursor-pointer">
            {{ t('fileListComponentOld.delete') }}
          </li>
        </ul>
      </div>
    </div>
  </div>
  <div v-if="props.showFilePreview && files.length === 0" class="flex justify-center">
    {{ t('fileListComponentOld.no_files_uploaded') }}
  </div>
</template>

<style scoped>

.file-preview-container {
  display: flex;
  flex-wrap: wrap;
}

.file-preview {
  max-width: 300px;
  min-width: 300px;
  max-height: 60px;
  border: 1px solid #e2e8f0;
  padding: 10px;
  display: flex;
  align-items: center;
  margin: 0 10px 10px 0;
  border-radius: 5px;
}

.file-icon {
  vertical-align: middle;
  background: rgb(187, 230, 103);
  background: linear-gradient(
      140deg,
      rgb(187, 230, 103) 0%,
      rgb(187, 230, 103) 100%
  );
  border: none;
  display: inline-block;
  width: 36px;
  height: 40px;
  line-height: 48px;
  text-align: center;
  border-radius: 5px;
  color: white;
  font-size: 0.9em;
  font-weight: 700;
  margin-right: 10px;
  min-width: 40px;
  min-height: 40px;
}

.file-icon__pdf {
  background: rgb(247, 85, 85);
  background: linear-gradient(
      140deg,
      rgb(247, 85, 85) 0%,
      rgb(213, 45, 45) 100%
  );
}

.file-icon__xlsx,
.file-icon__xls {
  background: rgb(45, 91, 58);
  background: linear-gradient(
      140deg,
      rgb(99, 193, 135) 0%,
      rgb(45, 91, 58) 100%
  );
}

.file-icon__docx,
.file-icon__doc {
  background: rgb(30, 62, 140);
  background: linear-gradient(
      140deg,
      rgb(93, 163, 232) 0%,
      rgb(30, 62, 140) 100%
  );
}

.file-icon__ppt,
.file-icon__pptx {
  background: rgb(196, 90, 59);
  background: linear-gradient(
      140deg,
      rgb(240, 149, 115) 0%,
      rgb(196, 90, 59) 100%
  );
}

</style>
